import { useMemo } from 'react';

function useRecipeFilters({
  recipes,
  inventory,
  searchTerms, // Only use committed search terms
  statusFilters,
  originFilters,
  favorites,
  bookmarks,
  originFilter,
  activeIngredientSet = null, // Add parameter for active ingredient set
  // debouncedSearchInput is no longer used for filtering
}) {
  // Create a cached inventory map for faster lookups
  // If there's an active ingredient set, use that instead of the regular inventory
  const inventoryMap = useMemo(() => {
    const map = new Map();
    
    // If we have an active ingredient set, use its ingredients
    if (activeIngredientSet && activeIngredientSet.ingredients) {
      // With a set, possessed is determined by presence in the set
      activeIngredientSet.ingredients.forEach(ingredientId => {
        map.set(ingredientId, true);
      });
    } else {
      // Without a set, use the regular inventory with possessed status
      inventory.forEach(ing => {
        if (ing.possessed) {
          map.set(ing.id, true);
        }
      });
    }
    
    return map;
  }, [inventory, activeIngredientSet]);

  // Extract unique origins once
  const uniqueOrigins = useMemo(() => {
    const origins = new Set(recipes.map(recipe => recipe.origin || 'Unknown'));
    return Array.from(origins).sort();
  }, [recipes]);

  // Create name counts map once
  const nameCounts = useMemo(() => {
    const counts = new Map();
    recipes.forEach(recipe => {
      counts.set(recipe.name, (counts.get(recipe.name) || 0) + 1);
    });
    return counts;
  }, [recipes]);

  // Compute missing ingredient counts once for all recipes
  const recipesWithMissingCounts = useMemo(() => {
    return recipes.map(recipe => {
      // Ensure we have ingredients array
      const ingredients = recipe.ingredients || [];
      // Count missing ingredients efficiently using the map
      const missingCount = ingredients.filter(ing => {
        const ingredientId = ing.ingredientId || ing.id;
        return !inventoryMap.has(ingredientId);
      }).length;
      return { ...recipe, missingCount };
    });
  }, [recipes, inventoryMap]);

  // Pre-compute lowercase search terms for efficiency
  const lowerSearchTerms = useMemo(() => {
    return searchTerms.map(term => term.toLowerCase());
  }, [searchTerms]);

  // Apply all filters efficiently
  const filteredRecipes = useMemo(() => {
    // Skip processing if there are no recipes
    if (recipesWithMissingCounts.length === 0) return [];
    
    // Create ingredient name lookup map
    const ingredientNameMap = new Map();
    inventory.forEach(ing => {
      ingredientNameMap.set(ing.id, ing.name?.toLowerCase() || '');
    });
    
    return recipesWithMissingCounts.filter(recipe => {
      // 1. Text search filtering
      if (lowerSearchTerms.length > 0) {
        const recipeNameLower = recipe.name.toLowerCase();
        const ingredients = recipe.ingredients || [];
        const ingredientNames = ingredients
          .map(ing => {
            const ingredientId = ing.ingredientId || ing.id;
            return ingredientNameMap.get(ingredientId);
          })
          .filter(Boolean);
        const recipeOriginLower = (recipe.origin || '').toLowerCase();
        
        // Check if all search terms match
        const allTermsMatch = lowerSearchTerms.every(term => 
          recipeNameLower.includes(term) || 
          ingredientNames.some(name => name.includes(term)) || 
          recipeOriginLower.includes(term)
        );
        
        if (!allTermsMatch) return false;
      }

      // 2. Status filtering (favorites, can-make, etc.)
      if (statusFilters.length > 0) {
        const statusMatch = statusFilters.some(filter => {
          switch (filter) {
            case 'favorites':
              return favorites?.has(recipe.id);
            case 'bookmarks':
              return bookmarks?.has(recipe.id);
            case 'can-make':
              return recipe.missingCount === 0;
            case 'missing-1':
              return recipe.missingCount === 1;
            case 'missing-2':
              return recipe.missingCount === 2;
            case 'missing-3':
              return recipe.missingCount === 3;
            default:
              return true;
          }
        });
        
        if (!statusMatch) return false;
      }

      // 3. Origin filtering
      if (originFilter) {
        return recipe.origin === originFilter;
      } else if (originFilters.length > 0) {
        if (!originFilters.includes(recipe.origin)) return false;
      }

      // Recipe passed all filters
      return true;
    });
  }, [
    recipesWithMissingCounts, 
    lowerSearchTerms, 
    statusFilters, 
    originFilters, 
    favorites, 
    bookmarks, 
    originFilter, 
    inventory
  ]);

  return { filteredRecipes, uniqueOrigins, nameCounts };
}

export default useRecipeFilters;